import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HelpCircle, X, MessageSquare, FileText, Flag } from 'lucide-react';
import './CommentsHelpButton.css';

export interface CommentsHelpButtonProps {
  /** Re-runs the interactive "comments moved" walkthrough. */
  onReplay: () => void;
  onReplayArchitectDecisions?: () => void;
}

/**
 * Persistent floating help affordance for the consolidated comments system.
 * Click opens a short summary card; "Replay the walkthrough" re-runs the
 * interactive tour. Sits below modals (z-index) so it never covers a popup,
 * and below the driver.js overlay so the tour always renders above it.
 */
const CommentsHelpButton: React.FC<CommentsHelpButtonProps> = ({ onReplay, onReplayArchitectDecisions }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleReplay = useCallback(() => {
    setOpen(false);
    onReplay();
  }, [onReplay]);

  return (
    <div className="cm-help" ref={rootRef}>
      {open && (
        <div className="cm-help-card" role="dialog" aria-label="Comments help">
          <div className="cm-help-card-head">
            <span className="cm-help-card-title">Comments &amp; notifications</span>
            <button
              type="button"
              className="cm-help-card-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              <X size={15} />
            </button>
          </div>
          <ul className="cm-help-list">
            <li>
              <MessageSquare size={14} />
              <span>
                Comments now live on each <strong>group line</strong> — one thread per group,
                unified across a deal’s Preconstruction and Under Contract phases.
              </span>
            </li>
            <li>
              <FileText size={14} />
              <span>
                The <strong>PDF Comments</strong> tab lists every PDF pin for the project — jump
                straight to one and mark it read or unread.
              </span>
            </li>
            <li>
              <Flag size={14} />
              <span>
                In group threads, set <strong>Action Required</strong> on each task in the table
                above the comment box. Elsewhere, use the flag on the task row.
              </span>
            </li>
          </ul>
          <button type="button" className="cm-help-replay" onClick={handleReplay}>
            Replay the walkthrough
          </button>
          {onReplayArchitectDecisions && (
            <button
              type="button"
              className="cm-help-replay"
              onClick={() => {
                setOpen(false);
                onReplayArchitectDecisions();
              }}
            >
              How asking the architect works
            </button>
          )}
        </div>
      )}
      <button
        type="button"
        className="cm-help-fab"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label="Comments help"
        title="Comments help"
      >
        <HelpCircle size={18} />
      </button>
    </div>
  );
};

export default CommentsHelpButton;
